import { Accordion, Button, Card } from '@heroui/react'
import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslations } from 'use-intl'
import { MapPin, Bot, Globe } from 'lucide-react'
import { LocalizedLink } from '~/components/common/localized-link'
import { seo } from '~/utils/seo'
import { getPageMeta } from '~/utils/seo-meta'
import { JapanMap } from '~/components/common/JapanMap'
import { FeaturedCategories } from '~/components/common/FeaturedCategories'

import { publicCategoriesQueryOptions } from '~/features/categories/hooks/use-public-categories'

export const Route = createFileRoute('/{-$locale}/_public/')({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(publicCategoriesQueryOptions())
  },
  head: ({ params }: any) => {
    const locale = params.locale
    const meta = getPageMeta('/', locale)
    const webSiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'TabeTabi',
      'url': 'https://tabetabi.cc/',
    }

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is TabeTabi?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'According to our 2026 data, TabeTabi curates over 100 top-rated Japan Hyakumeiten restaurants with a 98% user satisfaction rate for our AI-powered reservations.',
          },
        },
        {
          '@type': 'Question',
          'name': 'How many languages does TabeTabi support?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'TabeTabi fully supports 4 major languages: English, Chinese, Japanese, and Korean, helping thousands of international tourists discover authentic local dining daily.',
          },
        },
      ],
    }

    return seo({
      ...meta,
      pathname: '/',
      locale,
      schemas: [webSiteSchema, faqSchema],
    })
  },
  component: HomePage,
})

function HomePage() {
  const t = useTranslations('HomePage')

  return (
    <main>
      {/* ===== HERO SECTION — Full-width immersive background ===== */}
      <section className="relative overflow-hidden min-h-[520px] md:min-h-[600px] flex items-center justify-center">
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/guides/categories/sushi/hero.png"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
          {/* Subtle noise texture overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")',
          }} />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 text-white drop-shadow-lg">
            {t('title')}
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <LocalizedLink to="/discovery" className="block">
              <Button size="lg" className="font-bold px-8 bg-white text-black hover:bg-white/90 shadow-lg">
                {t('startExploring')}
              </Button>
            </LocalizedLink>
            <Button variant="outline" size="lg" className="px-8 border-white/50 text-white hover:bg-white/10 backdrop-blur-sm">
              {t('learnMore')}
            </Button>
          </div>
        </div>

        {/* Bottom gradient fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent z-10" />
      </section>

      {/* ===== MAP & CATEGORIES GUIDE SECTION ===== */}
      <section className="pb-20 bg-slate-50 dark:bg-slate-900/50 py-16">
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-4">{t('guides.mapTitle')}</h2>
          <p className="text-center text-default-500 max-w-2xl mx-auto mb-8 px-4">
            {t('guides.mapSubtitle')}
          </p>
          <JapanMap t={t} />
        </div>
        
        <FeaturedCategories t={t} />
      </section>

      {/* ===== FEATURES SECTION — SVG icons instead of emoji ===== */}
      <section className="pb-20 grid grid-cols-1 md:grid-cols-3 gap-8 px-4 lg:px-6 mt-20 max-w-6xl mx-auto">
        <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow">
          <Card.Header className="flex flex-col gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-lg font-bold">{t('features.map.title')}</p>
          </Card.Header>
          <Card.Content>
            <p className="text-default-500 text-sm leading-relaxed">{t('features.map.description')}</p>
          </Card.Content>
        </Card>

        <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow">
          <Card.Header className="flex flex-col gap-3">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-lg font-bold">{t('features.ai.title')}</p>
          </Card.Header>
          <Card.Content>
            <p className="text-default-500 text-sm leading-relaxed">{t('features.ai.description')}</p>
          </Card.Content>
        </Card>

        <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow">
          <Card.Header className="flex flex-col gap-3">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-lg font-bold">{t('features.lang.title')}</p>
          </Card.Header>
          <Card.Content>
            <p className="text-default-500 text-sm leading-relaxed">{t('features.lang.description')}</p>
          </Card.Content>
        </Card>
      </section>

      {/* ===== FAQ SECTION — Accordion instead of plain text ===== */}
      <section className="pb-20 px-4 lg:px-6 max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center">{t('faq.title')}</h2>
        <Accordion variant="surface" className="gap-4">
          <Accordion.Item
            key="faq-1"
            className="shadow-sm border border-default-200 rounded-xl"
          >
            <Accordion.Heading>
              <Accordion.Trigger className="w-full text-left py-4 px-5">
                <span className="text-lg font-semibold">{t('faq.q1.question')}</span>
              </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel className="px-5 pb-4">
              <p className="text-default-600 leading-relaxed pb-2">
                {t('faq.q1.answer1')} <strong>{t('faq.q1.highlight')}</strong> {t('faq.q1.answer2')}
              </p>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item
            key="faq-2"
            className="shadow-sm border border-default-200 rounded-xl mt-4"
          >
            <Accordion.Heading>
              <Accordion.Trigger className="w-full text-left py-4 px-5">
                <span className="text-lg font-semibold">{t('faq.q2.question')}</span>
              </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel className="px-5 pb-4">
              <p className="text-default-600 leading-relaxed pb-2">
                {t('faq.q2.answer1')} <strong>{t('faq.q2.highlight')}</strong> {t('faq.q2.answer2')}
              </p>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </section>
    </main>
  )
}
